import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useTheme, adminTheme } from '@/context/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Building2,
  Plus,
  Trash2,
  Edit,
  Save,
  X,
  ExternalLink,
  Image as ImageIcon,
  Globe,
  Loader2,
  RefreshCw,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';

export default function ClientManagement() {
  const { isDark } = useTheme();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', domain: '' });
  const [newClient, setNewClient] = useState({ name: '', domain: '' });
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/admin/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
      // Initialize with default clients if API fails
      setClients([
        { id: '1', name: 'Maybank', domain: 'maybank.com', active: true },
        { id: '2', name: 'Public Bank', domain: 'publicbank.com.my', active: true },
        { id: '3', name: 'CIMB', domain: 'cimb.com', active: true },
        { id: '4', name: 'AIA', domain: 'aia.com.my', active: true },
        { id: '5', name: 'Prudential', domain: 'prudential.com.my', active: true },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const getLogoUrl = (domain) => {
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
  };

  const handleAddClient = async () => {
    if (!newClient.name || !newClient.domain) {
      toast.error('Please fill in all fields');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/admin/clients`, newClient);
      setClients([...clients, response.data]);
      setNewClient({ name: '', domain: '' });
      setShowAddForm(false);
      toast.success('Client added successfully');
    } catch (error) {
      console.error('Error adding client:', error);
      // Fallback: add locally
      const newId = Date.now().toString();
      setClients([...clients, { id: newId, ...newClient, active: true }]);
      setNewClient({ name: '', domain: '' });
      setShowAddForm(false);
      toast.success('Client added locally');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateClient = async (id) => {
    if (!editForm.name || !editForm.domain) {
      toast.error('Please fill in all fields');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/admin/clients/${id}`, editForm);
      setClients(clients.map(c => c.id === id ? { ...c, ...editForm } : c));
      setEditingId(null);
      toast.success('Client updated successfully');
    } catch (error) {
      console.error('Error updating client:', error);
      // Fallback: update locally
      setClients(clients.map(c => c.id === id ? { ...c, ...editForm } : c));
      setEditingId(null);
      toast.success('Client updated locally');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClient = async (id) => {
    if (!confirm('Are you sure you want to remove this client?')) return;

    try {
      await axios.delete(`${API}/admin/clients/${id}`);
      setClients(clients.filter(c => c.id !== id));
      toast.success('Client removed successfully');
    } catch (error) {
      console.error('Error deleting client:', error);
      // Fallback: remove locally
      setClients(clients.filter(c => c.id !== id));
      toast.success('Client removed locally');
    }
  };

  const handleToggleActive = async (id, currentState) => {
    try {
      await axios.patch(`${API}/admin/clients/${id}/toggle`);
      setClients(clients.map(c => c.id === id ? { ...c, active: !currentState } : c));
      toast.success(currentState ? 'Client hidden from website' : 'Client visible on website');
    } catch (error) {
      // Fallback: toggle locally
      setClients(clients.map(c => c.id === id ? { ...c, active: !currentState } : c));
      toast.success(currentState ? 'Client hidden from website' : 'Client visible on website');
    }
  };

  const startEditing = (client) => {
    setEditingId(client.id);
    setEditForm({ name: client.name, domain: client.domain });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Client Logo Management</h1>
          <p className="text-muted-foreground">
            Manage client logos displayed on the website's "Our Clients" section
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchClients} data-testid="refresh-clients-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setShowAddForm(true)} data-testid="add-client-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Client
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Globe className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
            <div>
              <p className="text-sm text-blue-800 dark:text-blue-200 font-medium">
                Logo Source: Google Favicon Service
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
                Client logos are automatically fetched from their domain using Google's favicon service. 
                Simply enter the client's domain (e.g., "maybank.com") and the logo will be loaded automatically.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Client Form */}
      {showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Add New Client
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="client-name">Client Name</Label>
                <Input
                  id="client-name"
                  placeholder="e.g., Maybank"
                  value={newClient.name}
                  onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                  data-testid="new-client-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="client-domain">Domain</Label>
                <Input
                  id="client-domain"
                  placeholder="e.g., maybank.com"
                  value={newClient.domain}
                  onChange={(e) => setNewClient({ ...newClient, domain: e.target.value })}
                  data-testid="new-client-domain"
                />
              </div>
            </div>
            
            {/* Preview */}
            {newClient.domain && (
              <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                <span className="text-sm text-muted-foreground">Preview:</span>
                <img 
                  src={getLogoUrl(newClient.domain)} 
                  alt="Logo preview"
                  className="w-8 h-8 object-contain"
                  onError={(e) => { e.target.src = ''; e.target.style.display = 'none'; }}
                />
                <span className="text-sm">{newClient.name || 'Client Name'}</span>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAddForm(false)}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleAddClient} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Client
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Client List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Client List ({clients.length})</span>
            <Badge variant="secondary">
              {clients.filter(c => c.active !== false).length} Active
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {clients.map((client) => (
              <div 
                key={client.id}
                className={`p-4 rounded-lg border ${
                  client.active !== false 
                    ? 'bg-card border-border' 
                    : 'bg-muted/50 border-muted opacity-60'
                }`}
                data-testid={`client-card-${client.id}`}
              >
                {editingId === client.id ? (
                  <div className="space-y-3">
                    <Input
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      placeholder="Client name"
                      data-testid="edit-client-name"
                    />
                    <Input
                      value={editForm.domain}
                      onChange={(e) => setEditForm({ ...editForm, domain: e.target.value })}
                      placeholder="Domain"
                      data-testid="edit-client-domain"
                    />
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>
                        <X className="w-3 h-3" />
                      </Button>
                      <Button size="sm" onClick={() => handleUpdateClient(client.id)} disabled={saving}>
                        <CheckCircle2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-white dark:bg-slate-800 border flex items-center justify-center p-2">
                        <img 
                          src={getLogoUrl(client.domain)} 
                          alt={client.name}
                          className="w-8 h-8 object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                      <div>
                        <p className="font-medium">{client.name}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Globe className="w-3 h-3" />
                          {client.domain}
                        </p>
                        {client.active === false && (
                          <Badge variant="secondary" className="text-xs mt-1">Hidden</Badge>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => handleToggleActive(client.id, client.active !== false)}
                        title={client.active !== false ? 'Hide from website' : 'Show on website'}
                      >
                        {client.active !== false ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                        ) : (
                          <X className="w-4 h-4 text-muted-foreground" />
                        )}
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => startEditing(client)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => handleDeleteClient(client.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {clients.length === 0 && (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No clients added yet</p>
              <Button className="mt-4" onClick={() => setShowAddForm(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Client
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
